/* ============================================================================
   Deploy watcher. Open tabs keep running whatever bundle they loaded with — so
   a shipped change (e.g. a permission gate) doesn't reach anyone until they
   reload. This polls the served index.html (on focus + every 10 min), compares
   the bundle hash, and flips a flag so the UI can offer a one-tap refresh
   instead of silently running stale code.
   ========================================================================== */
import { useEffect, useState } from "react";

let updateReady = false;
const subs = new Set<() => void>();
let started = false;

function currentBundle(): string | null {
  const s = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]');
  const m = s?.src.match(/index-([\w-]+)\.js/);
  return m ? m[1] : null;
}

async function check() {
  if (updateReady) return;
  const mine = currentBundle();
  if (!mine) return;
  try {
    const res = await fetch("/", { cache: "no-store" });
    if (!res.ok) return;
    const html = await res.text();
    const m = html.match(/assets\/index-([\w-]+)\.js/);
    if (m && m[1] !== mine) {
      updateReady = true;
      subs.forEach((f) => f());
    }
  } catch { /* offline — try again later */ }
}

function start() {
  if (started || !import.meta.env.PROD) return;
  started = true;
  window.addEventListener("focus", () => void check());
  document.addEventListener("visibilitychange", () => { if (!document.hidden) void check(); });
  setInterval(() => void check(), 10 * 60_000);
  setTimeout(() => void check(), 15_000); // one early check after load settles
}

export function useUpdateReady(): boolean {
  const [ready, setReady] = useState(updateReady);
  useEffect(() => {
    start();
    const f = () => setReady(true);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return ready;
}

export async function applyUpdate() {
  try {
    // drop any cached shell/assets so the reload picks up the new build everywhere
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch { /* fine */ }
  window.location.reload();
}
